import { YES_NO, applicantSection, feeInfo, feeProofAttachment, otherAttachments, penaltyDeclaration, replySection } from "modules/ebiForms/common";
import { personFields, personsField, socialProxyAttachment, socialRepresentationSection } from "modules/ebiForms/definitions/sharedSocial";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C9. Wniosek o przyznanie Karty Dużej Rodziny lub wydanie duplikatu Karty Dużej Rodziny — art. 10 i 13 ustawy z 5.12.2014 r.
 * o Karcie Dużej Rodziny. Układ według wzoru KDR(7) MRPiPS (https://www.gov.pl/attachment/805fc40b-75bf-412a-a840-e3377f7d22ef,
 * pobrany 22.10.2026): dane wnioskodawcy, członkowie rodziny wielodzietnej (rodzaj wniosku dla każdego, rezygnacja z formy
 * tradycyjnej, status członka rodziny, planowany termin ukończenia nauki, adres), oświadczenie o umocowaniu, wyświetlanie
 * Kart elektronicznych przez rodziców (albo ZKDR-04), klauzula odpowiedzialności karnej (art. 10 ust. 6). Pominięto imiona
 * i nazwiska rodowe rodziców dziecka oraz stopień i ważność orzeczenia — zbędne przy numerze PESEL i dołączonym orzeczeniu.
 * Załączniki ZKDR-01–04 i dokumenty według katalogu i strony MRPiPS „Dokumenty potrzebne do złożenia wniosku”; dla osób
 * z PESEL gmina sprawdza dane w rejestrach. Opłata (art. 13): Karta bezpłatna; duplikat 17 zł (komunikat MRPiPS, od
 * 1.03.2026), tradycyjna forma Karty do posiadanej elektronicznej 10 zł (kwota ze wzoru KDR); zwolnienie z opłaty za duplikat
 * na umotywowany wniosek (art. 13 ust. 3).
 */
const DUPLICATE_FEE_WAIVED: EbiCondition = { field: "zwolnienieDuplikat", equals: true };

const C9: IEbiFormDefinition = {
  code: "C9",
  version: 1,
  title: "Wniosek o przyznanie Karty Dużej Rodziny lub wydanie duplikatu Karty",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Karta Dużej Rodziny (tradycyjna i elektroniczna w mObywatelu) dla rodziny wielodzietnej, duplikat Karty albo dodatkowa forma tradycyjna.",
  keywords: ["karta dużej rodziny", "KDR", "rodzina wielodzietna", "duplikat", "mObywatel", "3+"],
  organ: "Wójt / Burmistrz / Prezydent Miasta właściwy według miejsca zamieszkania wnioskodawcy — realizuje ośrodek pomocy społecznej lub centrum świadczeń",
  legalBasis: "art. 10 i art. 13 ustawy z dnia 5 grudnia 2014 r. o Karcie Dużej Rodziny",
  request: "Wnoszę o przyznanie Karty Dużej Rodziny lub wydanie duplikatu Karty Dużej Rodziny członkom rodziny wielodzietnej wskazanym we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Karta jest bezpłatna. Duplikat Karty — 17 zł (zwolnienie na umotywowany wniosek); tradycyjna forma Karty dla osoby, która ma już Kartę elektroniczną — 10 zł.",
  sections: [
    applicantSection(),
    socialRepresentationSection(),
    {
      code: "rodzinaWielodzietna",
      title: "Członkowie rodziny wielodzietnej",
      description:
        "Wpisz każdego członka rodziny wielodzietnej objętego wnioskiem — także siebie, jeśli wnosisz o Kartę dla siebie. Prawo do Karty mają rodzice i małżonek rodzica, którzy mają lub mieli na utrzymaniu łącznie co najmniej troje dzieci, oraz dzieci (do 18 lat, uczące się do 25 lat, z orzeczeniem o umiarkowanym albo znacznym stopniu niepełnosprawności bez ograniczenia wieku), jeżeli co najmniej troje dzieci w rodzinie spełnia te warunki.",
      fields: [
        feeInfo(
          "infoEmpatia",
          "Wniosek możesz złożyć także przez portal Emp@tia (wnioski.empatia.gov.pl). Aktywną Kartę — w każdej formie — można wyświetlać w aplikacji mObywatel.",
        ),
        personsField({
          code: "czlonkowie",
          label: "Członkowie rodziny wielodzietnej",
          itemLabel: "Członek rodziny",
          required: true,
          max: 20,
          fields: [
            {
              code: "wniosek",
              type: "select",
              label: "Dla tego członka rodziny wnoszę o",
              required: true,
              options: [
                { value: "przyznanie", label: "przyznanie Karty Dużej Rodziny" },
                { value: "nowyCzlonek", label: "przyznanie Karty dla nowego członka rodziny wielodzietnej" },
                { value: "ponownie", label: "przyznanie nowej Karty członkowi rodziny, który był wcześniej jej posiadaczem" },
                { value: "formaTradycyjna", label: "przyznanie dodatkowej, tradycyjnej formy Karty (posiada wyłącznie Kartę elektroniczną) — 10 zł" },
                { value: "duplikat", label: "wydanie duplikatu Karty — 17 zł" },
                { value: "brak", label: "nie wnoszę o przyznanie Karty" },
              ],
            },
            {
              code: "tylkoElektroniczna",
              type: "select",
              label: "Forma Karty",
              options: [
                { value: "obie", label: "tradycyjna i elektroniczna" },
                { value: "elektroniczna", label: "rezygnuję z tradycyjnej formy — tylko Karta elektroniczna" },
              ],
              hint: "Przy przyznaniu Karty. Późniejsze domówienie formy tradycyjnej kosztuje 10 zł.",
            },
            ...personFields().filter((f) => f.code !== "pokrewienstwo"),
            {
              code: "status",
              type: "select",
              label: "Członek rodziny jest",
              required: true,
              options: [
                { value: "rodzic", label: "rodzicem" },
                { value: "malzonek", label: "małżonkiem rodzica" },
                { value: "dziecko18", label: "dzieckiem w wieku do 18. roku życia" },
                { value: "dzieckoPiecza", label: "dzieckiem w wieku do 18. roku życia umieszczonym w rodzinie zastępczej lub rodzinnym domu dziecka" },
                { value: "dzieckoUczace", label: "dzieckiem w wieku powyżej 18. roku życia kontynuującym naukę w szkole lub szkole wyższej" },
                {
                  value: "osobaPiecza",
                  label: "osobą w wieku powyżej 18. roku życia, o której mowa w art. 37 ust. 2 ustawy o wspieraniu rodziny i systemie pieczy zastępczej",
                },
                { value: "dzieckoOrzeczenie", label: "dzieckiem w wieku powyżej 18. roku życia z orzeczeniem o umiarkowanym albo znacznym stopniu niepełnosprawności" },
                { value: "dzieckoNaUtrzymaniu", label: "dzieckiem, które było na utrzymaniu rodzica lub małżonka rodzica, niespełniającym obecnie warunków do Karty" },
              ],
            },
            { code: "terminNauki", type: "date", label: "Planowany termin ukończenia nauki", hint: "Dla dziecka powyżej 18 lat, które się uczy." },
            { code: "adres", type: "text", label: "Adres zamieszkania (jeśli inny niż wnioskodawcy)", maxLength: 300 },
          ],
        }),
      ],
    },
    {
      code: "okolicznosci",
      title: "Okoliczności wymagające dokumentów",
      fields: [
        {
          code: "wniosekRodzic",
          type: "radio",
          label: "Czy wniosek obejmuje przyznanie Karty rodzicowi (rodzicom)?",
          required: true,
          options: YES_NO,
        },
        {
          code: "dzieckoUczace",
          type: "radio",
          label: "Czy wniosek obejmuje dziecko w wieku powyżej 18 lat uczące się w szkole lub szkole wyższej?",
          required: true,
          options: YES_NO,
        },
        {
          code: "dzieckoOrzeczenie",
          type: "radio",
          label: "Czy wniosek obejmuje dziecko w wieku powyżej 18 lat z orzeczeniem o umiarkowanym albo znacznym stopniu niepełnosprawności?",
          required: true,
          options: YES_NO,
        },
        {
          code: "piecza",
          type: "radio",
          label:
            "Czy wniosek obejmuje dziecko umieszczone w rodzinie zastępczej lub rodzinnym domu dziecka albo osobę pozostającą w dotychczasowej pieczy po ukończeniu 18 lat?",
          required: true,
          options: YES_NO,
        },
        {
          code: "bezPesel",
          type: "radio",
          label: "Czy któremuś z członków rodziny objętych wnioskiem nie nadano numeru PESEL?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "cudzoziemiec",
          type: "radio",
          label: "Czy wniosek obejmuje cudzoziemca?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    {
      code: "oplata",
      title: "Opłata",
      fields: [
        {
          code: "liczbaDuplikatow",
          type: "number",
          label: "Liczba duplikatów Karty, o które wnoszę",
          width: "third",
          min: 1,
          max: 20,
          hint: "Pozostaw puste, jeśli nie wnosisz o duplikat.",
        },
        {
          code: "zwolnienieDuplikat",
          type: "checkbox",
          label: "Wnoszę o zwolnienie z opłaty za wydanie duplikatu (art. 13 ust. 3 ustawy)",
          visibleWhen: { field: "liczbaDuplikatow", filled: true },
        },
        {
          code: "zwolnienieUzasadnienie",
          type: "textarea",
          label: "Uzasadnienie wniosku o zwolnienie z opłaty",
          required: true,
          visibleWhen: { all: [{ field: "liczbaDuplikatow", filled: true }, DUPLICATE_FEE_WAIVED] },
          maxLength: 2000,
        },
        {
          code: "oplataDuplikat",
          type: "fee",
          label: "Opłata za duplikat",
          feeAmount: 17,
          feeUnitField: "liczbaDuplikatow",
          feeWaivedWhen: DUPLICATE_FEE_WAIVED,
          hint: "17 zł za każdy duplikat (od 1 marca 2026 r.).",
        },
        {
          code: "liczbaTradycyjnych",
          type: "number",
          label: "Liczba tradycyjnych form Karty domawianych do posiadanej Karty elektronicznej",
          width: "third",
          min: 1,
          max: 20,
          hint: "Pozostaw puste, jeśli nie domawiasz formy tradycyjnej.",
        },
        {
          code: "oplataTradycyjna",
          type: "fee",
          label: "Opłata za tradycyjną formę Karty",
          feeAmount: 10,
          feeUnitField: "liczbaTradycyjnych",
          hint: "10 zł za każdą Kartę.",
        },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "oswiadczenieUmocowanie",
          type: "declaration",
          label:
            "Oświadczam, że powyższe dane są prawdziwe oraz że jestem umocowany/umocowana do złożenia wniosku o przyznanie Karty Dużej Rodziny lub wydanie duplikatu Karty Dużej Rodziny oraz do odebrania Karty Dużej Rodziny lub duplikatu Karty Dużej Rodziny w imieniu członków rodziny wielodzietnej wskazanych w niniejszym wniosku.",
          required: true,
        },
        {
          code: "wyswietlanieKart",
          type: "checkbox",
          label: "Chcę, aby rodzic (rodzice) oraz małżonek rodzica mogli wyświetlać elektroniczne formy Karty wszystkich członków rodziny",
          hint: "Jeśli chcesz samodzielnie wybrać osoby, których Karty będą mogli wyświetlać rodzice lub małżonek rodzica, nie zaznaczaj pola — dołącz załącznik ZKDR-04.",
        },
        penaltyDeclaration(),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "zkdr03",
      label:
        "Oświadczenie rodzica, że nie jest i nie był pozbawiony władzy rodzicielskiej ani ograniczony w niej przez umieszczenie dziecka w pieczy zastępczej wobec co najmniej trojga dzieci (ZKDR-03)",
      hint: "Oświadczenie każdego rodzica objętego wnioskiem, podpisane przez niego.",
      visibleWhen: { field: "wniosekRodzic", equals: "tak" },
      required: true,
      multiple: true,
    },
    {
      code: "zkdr01",
      label: "Oświadczenie o planowanym terminie ukończenia nauki w szkole lub szkole wyższej (ZKDR-01)",
      visibleWhen: { field: "dzieckoUczace", equals: "tak" },
      required: true,
      multiple: true,
    },
    {
      code: "orzeczenie",
      label: "Orzeczenie o umiarkowanym albo znacznym stopniu niepełnosprawności dziecka",
      hint: "Kopia.",
      visibleWhen: { field: "dzieckoOrzeczenie", equals: "tak" },
      required: true,
      multiple: true,
    },
    {
      code: "postanowieniePiecza",
      label: "Postanowienie sądu o umieszczeniu dziecka w rodzinie zastępczej albo rodzinnym domu dziecka",
      hint: "Kopia. Dla osoby powyżej 18 lat pozostającej w dotychczasowej pieczy dołącz także oświadczenie ZKDR-02.",
      visibleWhen: { field: "piecza", equals: "tak" },
      required: true,
      multiple: true,
    },
    {
      code: "zkdr02",
      label: "Oświadczenie o pozostawaniu w dotychczasowej rodzinie zastępczej lub rodzinnym domu dziecka po ukończeniu 18 lat (ZKDR-02)",
      hint: "Tylko dla osoby powyżej 18 lat.",
      visibleWhen: { field: "piecza", equals: "tak" },
      multiple: true,
    },
    {
      code: "zkdr04",
      label: "Informacja o osobach, które będą mogły wyświetlać Kartę elektroniczną na swoich urządzeniach mobilnych (ZKDR-04)",
      hint: "Nieobowiązkowo — gdy chcesz samodzielnie wskazać te osoby.",
      visibleWhen: { not: { field: "wyswietlanieKart", equals: true } },
    },
    {
      code: "dokumentyBezPesel",
      label: "Dokumenty członków rodziny bez numeru PESEL — akt małżeństwa (małżonek rodzica), akt urodzenia lub dokument tożsamości (dziecko)",
      hint: "Kopie.",
      visibleWhen: { field: "bezPesel", equals: "tak" },
      required: true,
      multiple: true,
    },
    {
      code: "prawoPobytu",
      label: "Dokument potwierdzający prawo cudzoziemca do zamieszkania na terytorium Rzeczypospolitej Polskiej (np. karta pobytu)",
      hint: "Kopia (art. 10 ust. 5 ustawy).",
      visibleWhen: { field: "cudzoziemiec", equals: "tak" },
      required: true,
      multiple: true,
    },
    feeProofAttachment("Dowód wniesienia opłaty za duplikat lub tradycyjną formę Karty", {
      hint: "Potwierdzenie przelewu — numer rachunku podaje urząd.",
      visibleWhen: {
        any: [
          { all: [{ field: "liczbaDuplikatow", filled: true }, { not: DUPLICATE_FEE_WAIVED }] },
          { field: "liczbaTradycyjnych", filled: true },
        ],
      },
    }),
    socialProxyAttachment(),
    otherAttachments(),
  ],
};

export default C9;
